import React, { cloneElement } from 'react';
import { Box, Button, ButtonGroup, Tooltip } from '@mui/material';
import ArrowDropUpRounded from '@mui/icons-material/ArrowDropUpRounded';
import { arrayOf, bool, func, node, oneOfType, string } from 'prop-types';

import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuList from '@mui/material/MenuList';

const DisabledWrapper = ({ disabled, children }) =>
  disabled ? (
    <Tooltip
      title={
        <Box style={{ whiteSpace: 'pre-line' }}>
          Select one or more items to perform
          {'\n'}a bulk action.
        </Box>
      }
    >
      <span style={{ cursor: 'not-allowed', display: 'inline-block' }}>{children}</span>
    </Tooltip>
  ) : (
    <>{children}</>
  );

DisabledWrapper.propTypes = {
  children: node.isRequired,
  disabled: bool,
};

DisabledWrapper.defaultProps = {
  disabled: true,
};

const BulkActions = ({ actions, disabled, primaryActionButtonTitle }) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  return (
    <DisabledWrapper disabled={disabled}>
      <ButtonGroup
        aria-label="Button group with a nested menu"
        disabled={disabled}
        ref={anchorRef}
        variant="vertical-with-icon-outlined"
      >
        <Button
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          aria-label="bulk actions"
          endIcon={<ArrowDropUpRounded />}
          onClick={handleToggle}
          size="small"
          sx={{
            '& .MuiButton-endIcon svg': {
              fontSize: '1.5rem',
            },
          }}
          variant="vertical-with-icon-outlined"
        >
          {primaryActionButtonTitle}
        </Button>
      </ButtonGroup>
      <Popper
        anchorEl={anchorRef.current}
        disablePortal
        open={open}
        role={undefined}
        sx={{ zIndex: 10 }}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem
                  id="split-button-menu"
                  sx={{
                    '.MuiMenuItem-root': {
                      '&:hover': {
                        backgroundColor: 'palette._next.secondary._states.hover',
                      },
                      fontSize: '16px !important',
                      minHeight: 'auto',
                      px: 2,
                      py: 1,
                      width: '100%',
                    },
                  }}
                >
                  {actions &&
                    // eslint-disable-next-line react/no-array-index-key
                    actions.map((action, index) =>
                      // eslint-disable-next-line react/no-array-index-key
                      cloneElement(action, { key: `dialog-action-${index}` })
                    )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </DisabledWrapper>
  );
};

BulkActions.propTypes = {
  actions: arrayOf(node),
  disabled: bool,
  popoverContent: oneOfType([node, func]),
  primaryActionButtonTitle: string,
};

BulkActions.defaultProps = {
  actions: null,
  disabled: true,
  popoverContent: null,
  primaryActionButtonTitle: 'Bulk Actions',
};

const BulkActionFooter = ({ disabled, actions, primaryActionButtonTitle }) => {
  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        boxShadow:
          '0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.20)',
        display: 'flex',
        gap: 1,
        justifyContent: 'flex-end',
        mt: 'auto',
        pl: 2,
        position: 'relative',
        pr: 4,
        py: 2,
        zIndex: 10,
      }}
    >
      <BulkActions
        actions={actions}
        disabled={disabled}
        primaryActionButtonTitle={primaryActionButtonTitle}
      />
    </Box>
  );
};

BulkActionFooter.propTypes = {
  actions: oneOfType([node, func]),
  disabled: bool,
  primaryActionButtonTitle: string,
};

BulkActionFooter.defaultProps = {
  actions: null,
  disabled: true,
  primaryActionButtonTitle: 'Bulk Actions',
};

export default BulkActionFooter;
